import React, { useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../api/Api.js";
import UserPopup from "./UserPopup.js";
import DescriptionIcon from "@mui/icons-material/Description";
import UpdateIcon from "@mui/icons-material/Update";
import ProjectPopup from "./ProjectPopup.js";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";

const roles = [
  "Admin",
  "Super Admin",
  "User",
  "Financial Admin",
  "Administrator",
  "Accountant",
];

const nameConvertor = {
  administrator: "Administrator",
  financialadmin: "Financial Admin",
  admin: "Admin",
  superadmin: "Super Admin",
  user: "User",
  accountant: "Accountant",
};

const fetchUsers = async () => {
  const response = await apiClient.get(
    `${process.env.REACT_APP_NEW_DATA}/getUsers`
  );
  return response.data.users || [];
};

const addUser = async (newUser) => {
  const roleMapping = {
    Admin: "admin",
    "Financial Admin": "financialadmin",
    "Super Admin": "superadmin",
    User: "user",
    Accountant: "accountant",
    Administrator: "administrator",
  };

  const updatedUser = {
    ...newUser,
    role: roleMapping[newUser.role] || newUser.role,
  };

  const response = await apiClient.post(
    `${process.env.REACT_APP_AUTH_USER}/adduser`,
    updatedUser
  );
  if (response.status === 201) {
    toast.error(response.data.message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
    return;
  }
  return response.data;
};

const updateUser = async (updatedUser) => {
  const roleMapping = {
    Admin: "admin",
    "Financial Admin": "financialadmin",
    "Super Admin": "superadmin",
    User: "user",
    Accountant: "accountant",
    Administrator: "administrator",
  };

  const updatedUserWithMapped = {
    ...updatedUser,
    role: roleMapping[updatedUser.role] || updatedUser.role,
  };

  const response = await apiClient.put(
    `${process.env.REACT_APP_AUTH_USER}/updateuser/${updatedUser.user_id}`,
    updatedUserWithMapped
  );
  return response.data;
};

const AddUser = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [projectPopupOpen, setProjectPopupOpen] = useState(false);
  const [emailFilter, setEmailFilter] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    user_id: "",
    name: "",
    email: "",
    role: "",
    password: "",
  });

  const {
    data: users = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["users"],
    queryFn: fetchUsers,
  });

  const addMutation = useMutation({
    mutationFn: addUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      resetForm();
    },
  });

  const resetForm = () => {
    setFormData({ user_id: "", name: "", email: "", role: "", password: "" });
    setOpen(false);
    setIsEditMode(false);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = resetForm;

  const handlePopupClose = () => {
    setPopupOpen(false);
    setSelectedUser(null);
  };

  const handleProjectPopupClose = () => {
    setProjectPopupOpen(false);
    setSelectedUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleAddUser = () => {
    if (formData.name && formData.email && formData.role && formData.password) {
      addMutation.mutate(formData);
    }
  };

  const handleUpdateUser = () => {
    if (formData.name && formData.email && formData.role) {
      updateMutation.mutate(formData);
    }
  };

  const handleProjectClick = (user) => {
    setSelectedUser(user);
    setPopupOpen(true);
  };

  const handleAddProjectClick = (user) => {
    setSelectedUser(user);
    setProjectPopupOpen(true);
  };

  const handleEditUser = (user) => {
    setFormData({
      user_id: user.user_id,
      name: user.name,
      email: user.email,
      role: nameConvertor[user.role],
      password: "",
    });
    setIsEditMode(true);
    handleOpen();
  };

  const handleEmailFilterChange = (e) => {
    setEmailFilter(e.target.value);
  };

  const filteredUsers = users.filter((user) =>
    user.email.toLowerCase().includes(emailFilter.toLowerCase())
  );

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography>Loading users...</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={3}>
        <Typography>Error loading users.</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <h1>User Management</h1>
        <Button variant="contained" color="primary" onClick={handleOpen}>
          Add User
        </Button>
      </Box>

      <Box mb={2}>
        <TextField
          label="Filter by Email"
          value={emailFilter}
          onChange={handleEmailFilterChange}
          fullWidth
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>View Projects</TableCell>
              <TableCell>Add Projects</TableCell>
              <TableCell>Update</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user, index) => (
                <TableRow key={index}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{nameConvertor[user.role]}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleProjectClick(user)}
                    >
                      <DescriptionIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      onClick={() => handleAddProjectClick(user)}
                      disabled={nameConvertor[user.role] === "Administrator"}
                    >
                      Add Project
                    </Button>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleEditUser(user)}
                      disabled={nameConvertor[user.role] === "Administrator"}
                    >
                      <UpdateIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{isEditMode ? "Update User" : "Add User"}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="Role"
              name="role"
              select
              value={formData.role}
              onChange={handleInputChange}
              fullWidth
            >
              {roles.map((role, index) => (
                <MenuItem key={index} value={role}>
                  {role}
                </MenuItem>
              ))}
            </TextField>

            {!isEditMode && (
              <TextField
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={isEditMode ? handleUpdateUser : handleAddUser}
            variant="contained"
            color="primary"
            disabled={
              isEditMode ? updateMutation.isLoading : addMutation.isLoading
            }
          >
            {isEditMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <UserPopup
        open={popupOpen}
        user={selectedUser}
        onClose={handlePopupClose}
      />

      <ProjectPopup
        open={projectPopupOpen}
        user={selectedUser}
        onClose={handleProjectPopupClose}
        onSubmit={() => {
          setProjectPopupOpen(false);
          queryClient.invalidateQueries({ queryKey: ["users"] });
        }}
      />
    </Box>
  );
};

export default AddUser;
